import { useEffect, useState } from "react";
import { ArrowUpRight, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inquiryLabels = { general: "General Inquiry", class_pass: "Class Pass Registration", membership: "Private Class Bundle" };

const ContactSection = ({ selectedInquiry = "general" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", best_time: "Morning 9am-12pm EST", message: "" });
  const [inquiryType, setInquiryType] = useState(selectedInquiry);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setInquiryType(selectedInquiry), [selectedInquiry]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitContact = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, inquiry_type: inquiryType }),
      });
      if (!response.ok) throw new Error("contact");
      if (inquiryType === "class_pass" || inquiryType === "membership") {
        window.location.assign("/special-offers");
        return;
      }
      setSent(true);
      toast.success("Message sent", { description: "Tammam will review your inquiry and reply within 24 hours." });
    } catch (requestError) {
      setError("We couldn't send your message. Please try again.");
    }
  };

  return (
    <>
      <span id="contact" className="page-anchor" aria-hidden="true" />
      <section id="contact-section" className="contact-section section-space" aria-labelledby="contact-title">
      <Reveal className="section-inner contact-grid">
        <div className="contact-copy">
          <div className="section-kicker">LET’S TALK</div>
          <h2 id="contact-title">Have a question before you book?</h2>
          <p>Tell us what you want to feel more confident doing in Canada. We’ll point you toward the right first step.</p>
          <a className="contact-email" href="mailto:learn@myenglishlesson.ca" data-testid="contact-email-link"><Mail size={17} /> learn@myenglishlesson.ca <ArrowUpRight size={16} /></a>
          <div className="contact-note"><span>INQUIRY ROUTING</span>Choose a topic so your question reaches the right next step.</div>
        </div>
        {sent ? (
          <div className="contact-success" role="status" data-testid="contact-success-card">
            <div className="confirmation-icon"><Send size={23} /></div>
            <div><div className="section-kicker">MESSAGE SENT</div><h3>You're One Step Closer.</h3><p>Thank you for reaching out! We have received your inquiry, and Tammam will personally review it. Expect a reply in your inbox within 24 hours so we can map out your next steps together.</p></div>
          </div>
        ) : (
          <form className="contact-form" onSubmit={submitContact}>
            <fieldset className="inquiry-fieldset">
              <legend className="field-label">What are you inquiring about?</legend>
              <div className="inquiry-options" role="radiogroup" aria-label="Inquiry type">
                {Object.entries(inquiryLabels).map(([value, label]) => (
                  <label className={`inquiry-option ${inquiryType === value ? "is-selected" : ""}`} key={value}>
                    <input type="radio" name="inquiry_type" value={value} checked={inquiryType === value} onChange={(event) => setInquiryType(event.target.value)} data-testid={`inquiry-${value}-radio`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="field-label" htmlFor="contact-name">Name</label>
            <input className="field-input" id="contact-name" type="text" required value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Your name" data-testid="contact-input-name" />
            <label className="field-label" htmlFor="contact-email">Email</label>
            <input className="field-input" id="contact-email" type="email" required value={form.email} onChange={(event) => updateField("email", event.target.value)} placeholder="you@example.com" data-testid="contact-input-email" />
            <label className="field-label" htmlFor="contact-phone">Phone Number <span className="optional-label">(Optional)</span></label>
            <input className="field-input" id="contact-phone" type="tel" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} placeholder="Your phone number" data-testid="contact-input-phone" />
            <label className="field-label" htmlFor="contact-best-time">Best time to call/reach (EST)</label>
            <select className="field-input" id="contact-best-time" value={form.best_time} onChange={(event) => updateField("best_time", event.target.value)} data-testid="contact-input-best-time"><option value="Morning 9am-12pm EST">Morning 9am-12pm EST</option><option value="Afternoon 12pm-5pm EST">Afternoon 12pm-5pm EST</option><option value="Evening 5pm-8pm EST">Evening 5pm-8pm EST</option></select>
            <label className="field-label" htmlFor="contact-message">Your Message or Goal</label>
            <textarea className="field-input field-textarea" id="contact-message" required minLength={10} value={form.message} onChange={(event) => updateField("message", event.target.value)} placeholder="Tell us about your current English goals, upcoming job interviews, or any questions about our live coaching sessions..." data-testid="contact-input-message" />
            {error && <div className="form-error" role="alert" data-testid="contact-form-error">{error}</div>}
            <button className="button button-crimson contact-submit" type="submit" data-testid="contact-form-submit-button">Send message <Send size={16} /></button>
          </form>
        )}
      </Reveal>
      </section>
    </>
  );
};

export default ContactSection;
